import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/lib/i18n";
import { displayAuthorName } from "@/lib/anonAuthor";
import AppHeader from "@/components/AppHeader";
import BackButton from "@/components/BackButton";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { AlertTriangle, Clock, MapPin, CheckCircle2, XCircle, Timer } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function formatRemaining(seconds) {
  if (seconds == null || seconds < 0) return "-";
  if (seconds === 0) return "00:00:00";
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

export default function UrgentAlertDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const { t } = useI18n();
  const [alert, setAlert] = useState(null);
  const [remaining, setRemaining] = useState(0);

  const load = async () => {
    try { const { data } = await api.get(`/urgent-alerts/${id}`); setAlert(data); setRemaining(data.seconds_remaining || 0); }
    catch { toast.error(t("generic_error")); }
  };
  useEffect(() => { load(); }, [id]);
  useEffect(() => {
    if (!alert) return;
    const iv = setInterval(() => { setRemaining((s) => Math.max(0, s - 1)); }, 1000);
    return () => clearInterval(iv);
  }, [alert]);

  const confirm = async (kind) => {
    try { const { data } = await api.post(`/urgent-alerts/${id}/confirm`, { kind }); setAlert(data); toast.success(t("saved_confirmation")); }
    catch (e) { toast.error(e.response?.data?.detail || t("generic_error")); }
  };
  const [extendOpen, setExtendOpen] = useState(null); // hours to request
  const [extendReason, setExtendReason] = useState("");
  const [extendPhoto, setExtendPhoto] = useState(null); // {path,url}
  const [extendBusy, setExtendBusy] = useState(false);

  const uploadExtPhoto = async (file) => {
    try { const fd = new FormData(); fd.append("file", file); const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); setExtendPhoto({ path: data.path, url: `${BACKEND_URL}${data.url}` }); }
    catch { toast.error(t("photo_upload_failed")); }
  };

  const requestExtend = async () => {
    if (!extendOpen) return;
    if (!extendReason || extendReason.trim().length < 5) { toast.error("Motivazione richiesta (min 5 caratteri)"); return; }
    setExtendBusy(true);
    try {
      const { data } = await api.post(`/urgent-alerts/${id}/extend`, { hours: extendOpen, reason: extendReason, photo: extendPhoto?.path });
      setAlert(data); toast.success("Richiesta di proroga inviata");
      setExtendOpen(null); setExtendReason(""); setExtendPhoto(null);
    } catch (e) { toast.error(e.response?.data?.detail || t("generic_error")); }
    finally { setExtendBusy(false); }
  };

  const extend = requestExtend; // legacy alias

  if (!alert) return <div className="min-h-screen bg-[#F7F5F0]"><AppHeader /><div className="p-10 text-slate-500">{t("loading")}</div></div>;

  const state = alert.computed_state || alert.state;
  const stateColor = state === "active" ? "bg-red-100 text-red-800" : state === "expired_to_verify" ? "bg-yellow-100 text-yellow-800" : state === "resolved" ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-700";
  const stateLabelKey = state === "expired_to_verify" ? "urgent_state_expired" : state === "resolved" ? "urgent_state_resolved" : state === "archived" ? "urgent_state_archived" : state === "pending_review" ? "urgent_state_pending" : "urgent_state_active";

  return (
    <div className="min-h-screen bg-[#F7F5F0]" data-testid="urgent-detail-page">
      <AppHeader />
      <div className="max-w-3xl mx-auto px-4 md:px-8 py-8">
        <BackButton className="mb-4" testid="urgent-detail-back" />

        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
          {alert.photos?.[0] && (
            <img src={`${BACKEND_URL}/api/files/${alert.photos[0]}`} alt="urgent" className="w-full h-64 object-cover" />
          )}
          <div className="p-6">
            <div className="flex items-center gap-2 flex-wrap">
              <Badge className="bg-red-600 text-white border-0 uppercase tracking-widest text-[10px] font-semibold"><AlertTriangle size={10} className="mr-1" /> {t("urgent_alert")}</Badge>
              <Badge className={`${stateColor} border-0`} data-testid="urgent-state-badge">{t(stateLabelKey)}</Badge>
              <Badge variant="outline">{t(`cat_${alert.category}`) || alert.category}</Badge>
              <Badge variant="outline">{alert.subcategory === "other_urgent" ? t("urgent_other") : (t(`sub_${alert.subcategory}`) || alert.subcategory)}</Badge>
            </div>
            {alert.other_subcategory && <div className="mt-2 text-sm text-slate-700 italic">{alert.other_subcategory}</div>}
            <h1 className="font-heading text-2xl font-bold text-slate-900 mt-3">{alert.description}</h1>
            <div className="mt-3 text-xs text-slate-500 flex items-center gap-3 flex-wrap">
              <span className="flex items-center gap-1"><MapPin size={12} /> {alert.lat.toFixed(4)}, {alert.lng.toFixed(4)}</span>
              <span>·</span>
              <span>~{alert.radius_m} m</span>
              <span>·</span>
              <span>{t("of_author")} {displayAuthorName(alert, t)}</span>
              <span>·</span>
              <span>{new Date(alert.created_at).toLocaleString()}</span>
            </div>

            {state === "active" ? (
              <div className="mt-6 p-5 rounded-2xl bg-red-50 border border-red-200 flex items-center gap-4" data-testid="urgent-countdown">
                <Timer size={30} className="text-red-600" />
                <div className="flex-1">
                  <div className="text-xs uppercase tracking-widest text-red-800 font-semibold">{t("urgent_time_remaining")}</div>
                  <div className="font-heading font-bold text-3xl text-red-800 tabular-nums" data-testid="urgent-remaining">{formatRemaining(remaining)}</div>
                </div>
              </div>
            ) : (
              <div className="mt-6 p-4 rounded-xl bg-slate-100 text-sm text-slate-700 flex items-center gap-2"><Clock size={14} /> {t("urgent_expired")}</div>
            )}

            {user && state === "active" && (
              <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-2">
                <Button data-testid="urgent-still-present" onClick={() => confirm("still_present")} variant="outline" className="rounded-full h-11 hover:bg-red-50 hover:text-red-800">
                  <CheckCircle2 size={16} className="mr-2" /> {t("urgent_still_present")}
                  <span className="ml-2 text-xs text-slate-500">({alert.confirmations_still?.length || 0})</span>
                </Button>
                <Button data-testid="urgent-not-present" onClick={() => confirm("not_present")} variant="outline" className="rounded-full h-11 hover:bg-slate-900 hover:text-white">
                  <XCircle size={16} className="mr-2" /> {t("urgent_not_present")}
                  <span className="ml-2 text-xs text-slate-500">({alert.confirmations_not_present?.length || 0})</span>
                </Button>
              </div>
            )}

            {user && (user.user_id === alert.author_id || user.role === "admin" || user.role === "moderator") && (
              <div className="mt-4">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2">Richiedi proroga</div>
                <div className="flex flex-wrap gap-2">
                  {[1,3,6,12,24].map((h) => (
                    <Button key={h} data-testid={`urgent-extend-${h}`} size="sm" variant="outline" onClick={() => setExtendOpen(h)} className="rounded-full hover:bg-emerald-50 hover:text-emerald-800">+ {t(`urgent_${h}h`)}</Button>
                  ))}
                </div>
                {extendOpen && (
                  <div className="mt-3 p-4 rounded-2xl border border-emerald-200 bg-emerald-50/50 space-y-3" data-testid="extend-dialog">
                    <div className="text-sm font-semibold text-slate-900">Richiedi proroga di +{extendOpen}h</div>
                    <textarea data-testid="extend-reason" value={extendReason} onChange={(e) => setExtendReason(e.target.value)} placeholder="Motivazione obbligatoria (perché serve la proroga)…" className="w-full min-h-[80px] rounded-xl border border-slate-300 p-2 text-sm" />
                    {extendPhoto && <div className="relative w-40 rounded-lg overflow-hidden border border-slate-200"><img src={extendPhoto.url} alt="preview" className="w-full h-24 object-cover" /><button onClick={() => setExtendPhoto(null)} className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/60 text-white flex items-center justify-center text-xs">×</button></div>}
                    <div className="flex flex-wrap gap-2">
                      <label className="h-9 px-3 rounded-full bg-slate-100 hover:bg-slate-200 text-xs font-semibold flex items-center gap-1.5 cursor-pointer" data-testid="extend-take-photo"><input type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadExtPhoto(f); e.target.value=""; }} />Scatta foto</label>
                      <label className="h-9 px-3 rounded-full bg-slate-100 hover:bg-slate-200 text-xs font-semibold flex items-center gap-1.5 cursor-pointer" data-testid="extend-gallery"><input type="file" accept="image/*" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadExtPhoto(f); e.target.value=""; }} />Galleria</label>
                      <div className="flex-1" />
                      <Button data-testid="extend-submit" onClick={requestExtend} disabled={extendBusy} size="sm" className="rounded-full bg-emerald-600 hover:bg-emerald-700 hover:text-white text-white">{extendBusy ? "..." : "Invia richiesta"}</Button>
                      <Button variant="ghost" onClick={() => { setExtendOpen(null); setExtendReason(""); setExtendPhoto(null); }} size="sm" className="rounded-full hover:bg-slate-100 hover:text-slate-900">Annulla</Button>
                    </div>
                  </div>
                )}
                {alert.pending_extensions && alert.pending_extensions.some((e) => e.status === "pending") && (
                  <div className="mt-3 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-2">Proroga in attesa di approvazione</div>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="mt-4 text-xs text-slate-500">
          <Link to="/map" className="hover:text-emerald-700 underline">{t("back_to_map")}</Link>
        </div>
      </div>
    </div>
  );
}
